/**
 * SOLID Review
 * S — Strong. One use case: the email screen in sign-in mode — a code asked for, and its refusal worded by the decision.
 * O — Strong. The round trip runs it; how a refusal is worded is the Domain's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and auth-server ports and the round trip, injected.
 */
import type { IPayloadPortFailure } from '@ml3d/kernel';
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandEmail, ICommandLoginCodeAnswered, IPayloadEmail } from '../../domain/ports/schemas/AuthMoves';
import { SERVER_REFUSED } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepAuth } from './IConfigDepAuth';

/** What the server said to the request: nothing (a code is on its way), or a refusal in its own words — a rate limit. */
type TRefusal = TAnswered<string | null>;

/**
 * The email screen in sign-in mode (FE3; Q10, §28.248). The server answers the same for every address, so a code asked for
 * goes on to the code screen; its refusal (a rate limit) reaches the decision, which words it — so the call answers a
 * SERVER refusal as an answer — and a server that could not be asked is not the decision's to word.
 */
export class RequestLoginCodeUseCase implements IUseCase<ICommandEmail, TPortOutcome>, TServerRoundTrip<IPropsClientInput, ICommandEmail, IPayloadEmail, TRefusal> {
  constructor(private readonly deps: IConfigDepAuth) {}

  execute(ctx: IContextRequest, command: ICommandEmail): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmail) {
    return this.deps.decisions.planLoginCode(ctx, input, command);
  }

  async send(ctx: IContextRequest, plan: IPayloadEmail): Promise<TRefusal | IPayloadPortFailure> {
    const sent = await this.deps.auth.requestLoginCode(ctx, plan);
    if (!sent.success && sent.error.code !== SERVER_REFUSED) return sent;
    const said: TRefusal = { success: true, value: sent.success ? null : sent.error.message };
    return said;
  }

  answer(ctx: IContextRequest, input: IPropsClientInput, plan: IPayloadEmail, said: TRefusal) {
    const answered: ICommandLoginCodeAnswered = { email: plan.email, refusal: said.value };
    return this.deps.decisions.loginCodeAnswered(ctx, input, answered);
  }
}
